import React, { useState, useEffect, useMemo, useRef } from 'react'
import SVG from 'react-inlinesvg'
import moment from 'moment'
import {
  uniq,
  uniqBy,
  groupBy,
  sortBy,
  orderBy,
  capitalize,
  isEmpty,
} from 'lodash'
import {
  TTimeout,
  IChannelsV1List,
  Loadable,
  Load,
  SmallLoadable,
  ConnectNetworkModal,
  AdvertiserStatus,
  Logo,
  Header,
  Commission,
  Search,
  Tooltip,
  WithTooltip,
  useApiGet,
  useApiPost,
  useOutsideClick,
  useSortableTable,
  useCounter,
  useModal,
  useSnackbar,
  formatCommission,
  formatStat,
  formatCurrencyReversed,
  calculateValues,
  navigateTo,
  navigateReload,
  newTableSort,
  plural,
  map,
  isBlank,
  toArray,
  varClass,
} from '../shared'
import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest,
} from '../../services'
import {
  AssigneesModal,
  PrioritizeModal,
  DisableModal,
  SplitTestModal,
  CreatorModal,
  AllowPpcModal,
  IgnoresModal,
} from './Modals'
import { CampaignBoosts } from './CampaignBoosts'
import { CampaignsFilter } from './shared'

const maxTableLength = 100

const graphColors = [
  '#0C74DC',
  '#33CEC5',
  '#3FAC73',
  '#DA4554',
  '#E06D02',
  '#A2E989',
  '#8552EA',
  '#EBB517',

  '#4F9EEC',
  '#9765FB',
  '#F6DE97',
]

export const findEventCampaign = (collection: any, event: any) =>
  collection.find(
    (item) =>
      item.campaignUuid === event.campaign.campaignUuid ||
      item.campaign?.campaignUuid === event.campaign.campaignUuid
  )

const campaignFilterWithLimit = (
  collection: any,
  search: string,
  filters: any,
  limit?: number
): [any, boolean] => {
  const filtered = collection.filter((item, index) => {
    if (search) {
      const query = search.toLowerCase()
      const values = [
        item.campaign.campaignName,
        item.campaign.campaignId,
        item.campaign.campaignUrls?.join(' '),
        item.network.networkName,
        item.channels.map((channel) => channel.channelName)?.join(' '),
      ]
        .filter((value) => value)
        .map((value) => value.toLowerCase())
      if (values.every((value) => !value.includes(query))) return false
    }
    for (const filter of filters) {
      if (filter.value) {
        switch (filter.key) {
          case 'channelUuids':
            if (
              !item.channels.find((channel) =>
                filter.value.split(', ').includes(channel.channelUuid)
              )
            )
              return false
            break
          case 'networkUuids':
            if (!filter.value.split(', ').includes(item.network.networkUuid))
              return false
            break
          default:
            if (
              !filter.value
                .split(', ')
                .includes(item[filter.key.replace('Uuids', 'Uuid')])
            )
              return false
            break
        }
      }
    }
    return true
  })
  if (limit && filtered.length > limit) {
    return [
      filtered.filter((item, index) => {
        if (limit && index >= limit) return false
        return true
      }),
      true,
    ]
  } else {
    return [filtered, false]
  }
}

const campaignSort = newTableSort((a: any, b: any, key: string) => {
  if (key === 'networkName') {
    return [
      a.network.networkName + a.campaign.campaignId,
      b.network.networkName + b.campaign.campaignId,
    ]
  }
  if (key === 'performance') {
    if (a.performance.revenue && b.performance.revenue) {
      return [a.performance.revenue, b.performance.revenue]
    }
    // if (a.performance.conversionsCount && b.performance.conversionsCount) {
    //   return [a.performance.conversionsCount, b.performance.conversionsCount]
    // }
    return [a.performance.clicksCount, b.performance.clicksCount]
  }
  if (key === 'campaignCommission') {
    return [
      a.commissions.commissionFlat || a.commissions.commissionPercentage,
      b.commissions.commissionFlat || b.commissions.commissionPercentage,
    ]
  }
})

const trafficSort = newTableSort()

export const Merchant2 = (props) => {
  const { advertiserUuid } = props
  const [campaignReloadRef, reloadCampaign] = useCounter()
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState([
    {
      key: 'channelUuids',
      value: '',
      label: '',
    },
    {
      key: 'networkUuids',
      value: '',
      label: '',
    },
  ])
  const filterReset = filters
    .filter((filter) => filter.value)
    .map((filter) => filter.value)
    .join(',')

  const channels = useApiGet<IChannelsV1List[]>('/channels/v1/list')
  const networks = useApiGet('/networks/v1/list')
  const networkDetails = useApiGet('/networks/v1/networks')

  const [assigneeRef, reloadAssignees] = useCounter()
  const assignees = useApiGet<any[]>(
    `/advs/v1/assignees/`,
    { advertiserUuid },
    [assigneeRef]
  )

  const [ignoreRef, reloadIgnores] = useCounter()
  const ignores = useApiGet<any[]>(`/advs/v1/ignores/`, { advertiserUuid }, [
    ignoreRef,
  ])

  const data = useApiGet(`/advs/v1/advs/${advertiserUuid}`)
  const campaigns = useApiPost<any>(
    `/advs/v2/advs/${advertiserUuid}/camps`,
    {},
    [campaignReloadRef]
  )
  const traffic = useApiPost(
    '/insights/v1/statistics',
    {
      groupBy: 'page',
      fromDate: moment().subtract(30, 'days').toDate(),
      toDate: new Date(),
      filters: {
        advertiserUuids: [advertiserUuid],
        ...filters.reduce((result, filter) => {
          if (filter.value) result[filter.key] = filter.value.split(', ')
          return result
        }, {}),
      },
    },
    [advertiserUuid, filterReset]
  )

  const channelsMap = useMemo(() => {
    if (channels) {
      return channels.reduce((result, channel) => {
        result[channel.channelUuid] = channel
        return result
      }, {})
    } else {
      return {}
    }
  }, [channels])

  return (
    <>
      <Header backPath="/merchants" />
      <Loadable data={data && campaigns && channels && networks}>
        <Content
          data={data}
          campaigns={toArray(campaigns)}
          {...{
            channels,
            channelsMap,
            networks,
            networkDetails,
            advertiserUuid,
            assignees,
            reloadAssignees,
            ignores,
            reloadIgnores,
            traffic,
            search,
            setSearch,
            filters,
            setFilters,
            reloadCampaign,
            campaignReloadRef,
          }}
        />
      </Loadable>
    </>
  )
}

const Content = (props) => {
  const {
    data,
    campaigns,
    comments,
    channels,
    channelsMap,
    networks,
    networkDetails,
    advertiserUuid,
    assignees,
    reloadAssignees,
    ignores,
    reloadIgnores,
    traffic,
    search,
    setSearch,
    filters,
    setFilters,
    reloadCampaign,
    campaignReloadRef,
  } = props

  if (isEmpty(data)) {
    getRequest('/advs/v1/search', {
      advertiserDomain: advertiserUuid,
    }).then((response) => {
      const searchAdvertisers = response?.data || []
      const searchAdvertiser =
        searchAdvertisers.find(
          (advertiser) =>
            advertiser.advertiserDomain === advertiserUuid.toLowerCase()
        ) ||
        sortBy(
          searchAdvertisers,
          (advertiser) => advertiser.advertiserDomain.length
        )[0]
      if (searchAdvertiser) {
        window.location.pathname = `/merchants/${searchAdvertiser.advertiserUuid}`
      }
    })
    return null
  }

  const { advertiser, performance } = data

  const [tab, setTab] = useState('')

  const [connectedNetworks, activeCampaigns, otherCampaigns, closedCampaigns] =
    useMemo(() => {
      if (!campaigns?.length) {
        return [[], [], [], []]
      }

      const connectedNetworks = uniqBy(
        campaigns
          .filter((campaign) => campaign.isConnected)
          .map((campaign) => campaign.network),
        (network: any) => network.networkUuid
      )
      const activeCampaigns = campaigns.filter(
        (campaign) =>
          campaign.campaign.campaignIsActive &&
          (campaign.performance.clicksCount ||
            campaign.performance.conversionsCount ||
            campaign.performance.revenue)
      )
      const otherCampaigns = campaigns.filter(
        (campaign) =>
          campaign.campaign.campaignIsActive &&
          !(
            campaign.performance.clicksCount ||
            campaign.performance.conversionsCount ||
            campaign.performance.revenue
          )
      )
      const closedCampaigns = campaigns.filter(
        (campaign) => !campaign.campaign.campaignIsActive
      )

      return [
        connectedNetworks,
        activeCampaigns,
        otherCampaigns,
        closedCampaigns,
      ]
    }, [campaigns])

  const { showModal } = useModal()
  const { showSnackbar } = useSnackbar()

  const [creatorsStatus, setCreatorsStatus] = useState<
    'open' | 'approval' | 'disabled'
  >(
    advertiser.advertiserCreatorApproval === 1
      ? 'approval'
      : advertiser.advertiserCreatorDisabled === 1
      ? 'disabled'
      : 'open'
  )

  const histories = useApiPost(
    `/advs/v1/advs/${advertiser.advertiserUuid}/camps/histories`,
    {
      fromDate: new Date(
        advertiser.advertiserCreatedDatetime.replace(' ', 'T')
      ),
      toDate: new Date(),
    }
  )

  const [boostsRef, reloadBoosts] = useCounter()
  const boosts = useApiGet(`/advs/v1/boosts/${advertiser.advertiserUuid}`, {}, [
    boostsRef,
  ])

  return (
    <div className="advertiser-show-content">
      <div className="advertiser-info">
        <div className="card">
          <div className="card-body p-t-10 p-b-1">
            <div className="row row-center row-fill">
              <Logo
                src={advertiser.advertiserFaviconUrl}
                width={32}
                height={32}
                className="m-r-3"
                bordered
              />
              <span className="text-dark text-big text-bolder m-r-2">
                {advertiser.advertiserDomain}
              </span>
              <a
                href={`https://${advertiser.advertiserDomain}`}
                target="_blank"
                rel="noreferrer"
                className="link link-lighter m-t-05"
              >
                <SVG src="/images/icon-outside.svg" />
              </a>
            </div>
            <div className="m-t-5">
              <div
                className="row row-center row-space-between"
                style={{ minHeight: 25 }}
              >
                <div className="text-light text-small text-bold">
                  Date added:
                </div>
                <div className="text-dark">
                  {advertiser.advertiserCreatedDatetime.split(' ')[0]}
                </div>
              </div>
            </div>
            {advertiser.categoryName && (
              <div className="m-t-3">
                <div className="row row-center row-space-between">
                  <span className="text-light text-small text-bold">
                    Category:
                  </span>
                  {advertiser.categoryName}
                </div>
              </div>
            )}
            {connectedNetworks.length > 0 && (
              <div className="m-t-3">
                <div className="row row-center row-space-between row-nowrap">
                  <span className="text-light text-small text-bold text-nowrap">
                    Found on:
                  </span>
                  <div className="text-right">
                    {map(connectedNetworks, (network) => (
                      <span
                        key={network.networkUuid}
                        className="badge badge-hoverable text-smaller text-nowrap m-05"
                        onClick={(event) => {
                          navigateTo(event, `/networks/${network.networkUuid}`)
                        }}
                      >
                        {network.networkName}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
            <div className="m-t-3">
              <div
                className="row row-center row-space-between row-nowrap"
                style={{ minHeight: 25 }}
              >
                <span className="text-light text-small text-bold text-nowrap">
                  Assigned to:
                </span>
                <div className="text-right">
                  <button
                    className="btn btn-icon btn-borderless btn-xs"
                    style={{
                      minWidth: 20,
                      width: 20,
                      minHeight: 20,
                      height: 20,
                    }}
                    onClick={() => {
                      showModal(
                        <AssigneesModal
                          advertiser={data.advertiser}
                          assignees={assignees}
                          onDone={reloadAssignees}
                        />,
                        {
                          cardClassName: 'prioritize-modal',
                        }
                      )
                    }}
                  >
                    <SVG
                      src="/images/icon-plus2-color.svg"
                      width="14"
                      height="14"
                      style={{ position: 'relative', top: -2 }}
                    />
                  </button>
                  {map(assignees, (assignee) => (
                    <span
                      key={assignee.assigneeUserUuid}
                      className="badge badge-hoverable text-smaller text-nowrap m-05"
                      title={`${assignee.assigneeUserFirstname} ${assignee.assigneeUserLastname}`}
                    >
                      {assignee.assigneeUserFirstname}{' '}
                      {assignee.assigneeUserLastname[0]}.
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="m-t-3">
              <div className="row row-center row-space-between">
                <span className="text-light text-small text-bold">
                  Partner level:
                </span>
                <div>
                  <AdvertiserStatus
                    advertiserUuid={data.advertiser.advertiserUuid}
                    statusType="partner"
                    initialStatus={
                      data.advertiser.advertiserStatuses?.partner || ''
                    }
                  />
                </div>
              </div>
            </div>
            <div className="m-t-3">
              <div className="row row-center row-space-between">
                <span className="text-light text-small text-bold">
                  Deal type:
                </span>
                <div>
                  <AdvertiserStatus
                    advertiserUuid={data.advertiser.advertiserUuid}
                    statusType="deal"
                    initialStatus={
                      data.advertiser.advertiserStatuses?.deal || ''
                    }
                  />
                </div>
              </div>
            </div>
            <div className="m-t-3">
              <div className="row row-center row-space-between">
                <span className="text-light text-small text-bold">
                  Creators:
                </span>
                <div>
                  <span
                    className={varClass({
                      'badge badge-hoverable text-smaller text-nowrap m-05':
                        true,
                      'text-green': creatorsStatus === 'open',
                      'text-orange': creatorsStatus === 'approval',
                      'text-red': creatorsStatus === 'disabled',
                    })}
                    onClick={async () => {
                      const newCreatorsStatus =
                        creatorsStatus === 'open'
                          ? 'approval'
                          : creatorsStatus === 'approval'
                          ? 'disabled'
                          : 'open'
                      setCreatorsStatus(newCreatorsStatus)
                      try {
                        await postRequest(
                          `/advs/v1/advs/${advertiser.advertiserUuid}/creators`,
                          {
                            advertiserCreatorApproval:
                              newCreatorsStatus === 'approval',
                            advertiserCreatorDisabled:
                              newCreatorsStatus === 'disabled',
                          }
                        )
                      } catch (err) {
                        console.error(err)
                        setCreatorsStatus(creatorsStatus)
                      }
                    }}
                  >
                    {capitalize(creatorsStatus)}
                  </span>
                </div>
              </div>
            </div>

            <div className="card-divider" />

            <div className="text-dark text-bold m-t-6">Performance 30 days</div>
            <div className="row row-center row-space-between m-t-4">
              <span className="text-light text-small text-bold">Clicks:</span>
              <span className="value-badge value-badge-clickCount">
                {formatStat(performance.clicksCount)}
              </span>
            </div>
            <div className="row row-center row-space-between m-t-4">
              <span className="text-light text-small text-bold">
                Conversions:
              </span>
              <span className="value-badge value-badge-conversionCount">
                {formatStat(performance.conversionsCount)}
              </span>
            </div>
            <div className="row row-center row-space-between m-t-4">
              <span className="text-light text-small text-bold">Revenue:</span>
              <span className="value-badge value-badge-conversionCommission">
                {formatCurrencyReversed(
                  performance.revenue,
                  performance.currencyCode || 'DKK'
                )}
              </span>
            </div>
          </div>
        </div>
        <div className="card m-t-20">
          <div className="card-body p-t-0 p-b-1">
            <div className="text-dark text-big text-bolder m-b-4">Notes</div>
            <Comments advertiserUuid={advertiserUuid} />
          </div>
        </div>
        <div className="card m-t-20">
          <div className="card-body p-t-0 p-b-1">
            <div className="text-dark text-big text-bolder m-b-4">
              ⚡ EPC Routing
            </div>
            <EpcRouting advertiserUuid={advertiserUuid} />
          </div>
        </div>
      </div>

      <div className="advertiser-data">
        <div className="card p-b-0 m-b-20">
          {/*<div className="advertiser-tab-select">
            {map(
              {
                '': 'Overview',
                'traffic': 'Traffic',
              },
              (value, label) => (
                <button
                  key={value}
                  className={varClass({
                    'link': true,
                    'active': tab === value,
                  })}
                  onClick={() => setTab(value)}
                >
                  {label}
                </button>
              )
            )}
          </div>*/}

          <div className="card-body p-t-10">
            <div className="row row-center row-space-between m-b-7">
              <div className="vertical-middle">
                <CampaignSearch {...{ search, setSearch }} />
                <CampaignsFilter
                  {...{ filters, setFilters, channels, networks }}
                />
              </div>
              <CampaignsIgnoreButton
                {...{
                  advertiser,
                  ignores,
                  reloadIgnores,
                  networks,
                  channels,
                  showModal,
                }}
              />
            </div>

            {tab === '' && (
              <>
                <Table
                  title="Campaigns with traffic the last 30 days"
                  campaigns={activeCampaigns}
                  allCampaigns={[...activeCampaigns, ...otherCampaigns]}
                  advertiserUuid={advertiserUuid}
                  networkDetails={networkDetails}
                  search={search}
                  filters={filters}
                  channels={channels}
                  channelsMap={channelsMap}
                  showModal={showModal}
                  showSnackbar={showSnackbar}
                  reloadCampaign={reloadCampaign}
                  campaignReloadRef={campaignReloadRef}
                />
                <Table
                  title="Other campaigns"
                  campaigns={otherCampaigns}
                  allCampaigns={[...activeCampaigns, ...otherCampaigns]}
                  networkDetails={networkDetails}
                  advertiserUuid={advertiserUuid}
                  search={search}
                  filters={filters}
                  channels={channels}
                  channelsMap={channelsMap}
                  showModal={showModal}
                  showSnackbar={showSnackbar}
                  reloadCampaign={reloadCampaign}
                  campaignReloadRef={campaignReloadRef}
                  className="m-t-40"
                />
                <ClosedCampaignTable
                  title="Closed campaigns"
                  campaigns={closedCampaigns}
                  networkDetails={networkDetails}
                  search={search}
                  filters={filters}
                  channels={channels}
                  channelsMap={channelsMap}
                  showModal={showModal}
                  showSnackbar={showSnackbar}
                  reloadCampaign={reloadCampaign}
                  className="m-t-40"
                />
              </>
            )}

            {tab === 'traffic' && (
              <>
                <SmallLoadable loaded={traffic}>
                  <TrafficTable
                    title="Campaigns with traffic the last 30 days"
                    data={traffic}
                    channels={channels}
                  />
                </SmallLoadable>
              </>
            )}
          </div>
        </div>

        <div className="card p-b-0 m-b-20">
          <div className="card-body p-t-10">
            <SmallLoadable loaded={histories} height={80} className="m-b-60">
              <CampaignHistory
                data={histories}
                {...{ campaigns, channels, networks }}
              />
            </SmallLoadable>
          </div>
        </div>

        <div className="card p-b-0 m-b-160">
          <div className="card-body p-t-10">
            <SmallLoadable loaded={boosts} height={80} className="m-b-60">
              <CampaignBoosts
                data={boosts}
                {...{
                  advertiserUuid,
                  channels,
                  channelsMap,
                  reloadBoosts,
                  showModal,
                }}
              />
            </SmallLoadable>
          </div>
        </div>
      </div>
    </div>
  )
}

const Comments = (props) => {
  const { advertiserUuid } = props

  const [reloadRef, reload] = useCounter()
  const comments = useApiGet<any[]>(`/advs/v1/comments/${advertiserUuid}`, {}, [
    reloadRef,
  ])

  const [submitting, setSubmitting] = useState(false)
  const [shownComments, setShownComments] = useState([])
  const [newComment, setNewComment] = useState('')
  const [editCommentUuid, setEditCommentUuid] = useState('')

  useEffect(() => {
    if (comments) {
      setShownComments(toArray(comments))
    }
  }, [comments])

  const onSubmit = async () => {
    if (!newComment.trim()) return
    if (submitting) return
    setSubmitting(true)

    if (editCommentUuid) {
      await putRequest(
        `/advs/v1/comments/${advertiserUuid}/${editCommentUuid}`,
        {
          content: newComment,
        }
      )
      const newShownComments = [...shownComments]
      const editedIndex = newShownComments.findIndex(
        (comment) => comment.commentUuid === editCommentUuid
      )
      if (editedIndex > -1) {
        newShownComments[editedIndex].commentText = newComment
        newShownComments[editedIndex].commentUpdatedDatetime =
          moment().toString()
        setShownComments(newShownComments)
      }
    } else {
      await postRequest(`/advs/v1/comments/${advertiserUuid}`, {
        content: newComment,
      })
      setShownComments([
        {
          commentUuid: '',
          commentText: newComment,
          commentAuthor: '',
          commentUpdatedDatetime: moment().toString(),
        },
        ...shownComments,
      ])
    }

    setNewComment('')
    setEditCommentUuid('')
    reload()
    setSubmitting(false)
  }

  const onDelete = async (uuid: string) => {
    if (submitting) return
    setSubmitting(true)
    await deleteRequest(`/advs/v1/comments/${advertiserUuid}/${uuid}`)
    setShownComments(
      shownComments.filter((comment) => comment.commentUuid !== uuid)
    )
    reload()
    setSubmitting(false)
  }

  return (
    <div>
      <Loadable
        data={comments}
        loaded={shownComments.length}
        placeholder={
          <div className="m-b-4">
            <Load width={200} height={17} className="m-b-2" />
            <Load width={160} height={16} className="m-b-2" />
          </div>
        }
      >
        {map(shownComments, (comment) => (
          <div key={comment.commentUuid} className="advertiser-note m-b-4">
            <div className="m-b-2">
              {comment.commentText
                .split('\n')
                .map((line: string, index: number) => (
                  <div key={comment.commentUuid + index}>{line}</div>
                ))}
            </div>
            <div className="row row-center row-space-between text-light text-small">
              <i>{comment.commentAuthor}</i>
              <div>
                {moment(comment.commentUpdatedDatetime).format(
                  'YYYY-MM-DD HH:mm'
                )}
              </div>
            </div>
            {!!comment.commentUuid && (
              <div className="note-actions">
                <button
                  className="link btn-borderless p-x-1"
                  onClick={(event) => {
                    setEditCommentUuid(comment.commentUuid)
                    setNewComment(comment.commentText)
                    const buttonEl = event.target as HTMLButtonElement
                    buttonEl
                      .closest('.advertiser-note')
                      .parentElement.querySelector('textarea')
                      .focus()
                  }}
                >
                  <SVG src="/images/icon-edit.svg" width={16} />
                </button>
                <button
                  className="link btn-borderless link-danger p-x-1"
                  onClick={() => onDelete(comment.commentUuid)}
                >
                  <SVG src="/images/icon-trash.svg" width={16} />
                </button>
              </div>
            )}
          </div>
        ))}
      </Loadable>

      <div
        className={varClass({
          'control w-100 m-b-2': true,
          'm-t-4': shownComments?.length || comments === null,
        })}
      >
        <textarea
          className="w-100"
          value={newComment}
          onChange={(event) => setNewComment(event.target.value)}
        />
      </div>
      <div className="row row-center row-end">
        <div>
          {submitting && (
            <SmallLoadable loaded={false} style={{ width: 16, height: 16 }} />
          )}
        </div>
        {editCommentUuid ? (
          <>
            <button
              className="btn btn-md"
              onClick={() => {
                setEditCommentUuid('')
                setNewComment('')
              }}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              className="btn btn-md"
              onClick={onSubmit}
              disabled={submitting}
            >
              Update
            </button>
          </>
        ) : (
          <button
            className="btn btn-md"
            onClick={onSubmit}
            disabled={submitting}
          >
            Save
          </button>
        )}
      </div>
    </div>
  )
}

const EpcRouting = (props) => {
  const { advertiserUuid } = props
  const routing = useApiGet<any>(`/epc/v1/routing/${advertiserUuid}`)

  const groups = routing?.groups || []

  if (routing && !groups.length) {
    return <div className="text-light text-small">No EPC routing data</div>
  }

  return (
    <Loadable
      data={routing}
      placeholder={
        <div className="m-b-4">
          <Load width={200} height={17} className="m-b-2" />
          <Load width={160} height={16} className="m-b-2" />
        </div>
      }
    >
      {groups.map((group, gi) => (
        <div key={gi} className={gi > 0 ? 'm-t-6' : ''}>
          <div
            className="text-small text-bold"
            style={{ fontFamily: 'monospace' }}
          >
            {group.channelName} / {group.countryCode}{' '}
            <span className="text-light">{group.mode}</span>
          </div>
          <div
            className="m-t-2"
            style={{
              fontFamily: 'monospace',
              fontSize: 12,
              lineHeight: '18px',
            }}
          >
            {group.trackers.map((tracker, ti) => {
              const isLast = ti === group.trackers.length - 1
              const prefix = isLast ? '\u2514\u2500' : '\u251C\u2500'
              const outer = isLast ? '\u00A0\u00A0' : '\u2502\u00A0'
              const weightPct = Math.round(tracker.weight * 100)
              const winPct = (tracker.winProbability * 100).toFixed(1)
              return (
                <div key={tracker.trackerUuid}>
                  <div className="text-small">
                    <span className="text-light">{prefix} </span>
                    <span className="text-bold">{tracker.networkName}</span>
                    {tracker.isChampion ? ' 👑' : ''}
                  </div>
                  <div className="text-smaller text-light">
                    {outer} {'\u251C\u2500'} weight: {weightPct}%
                  </div>
                  <div className="text-smaller text-light">
                    {outer} {'\u251C\u2500'} win probability: {winPct}%
                  </div>
                  <div className="text-smaller text-light">
                    {outer} {'\u2514\u2500'} EPC{' '}
                    {tracker.effectiveEpc.toFixed(2)} DKK
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </Loadable>
  )
}

const CampaignSearch = (props) => {
  const { search, setSearch } = props
  const [value, setValue] = useState(search)

  const valueRef = useRef<TTimeout>()
  useEffect(() => {
    if (valueRef.current) {
      clearTimeout(valueRef.current)
    }
    valueRef.current = setTimeout(() => {
      setSearch(value)
      valueRef.current = null
    }, 650)
  }, [value])

  return (
    <>
      <Search
        value={value}
        setValue={setValue}
        onSubmit={() => {
          setSearch(value)
        }}
        placeholder="Search"
        className="search w-30 m-r-15"
        inputClassName="w-100 p-y-15 p-r-8 campaign-search-input"
        highlight={!!value}
      />
      <button
        className={varClass({
          'link link-new-lighter': true,
          'o-0 unclickable': !value,
          'o-100': value,
        })}
        style={{
          position: 'relative',
          top: 2,
          left: -36,
          paddingTop: 4,
          marginTop: -4,
          marginRight: -32,
        }}
        onClick={() => {
          setValue('')
          setSearch('')
        }}
      >
        <SVG src="/images/icon-times.svg" />
      </button>
    </>
  )
}

const allowedUserUuids = [
  '8fafd4c0-ec5a-4551-8037-5d5810ae65cd', // dev
  '2e60f654-a7e9-4ff1-8205-4019f0021d59', // Simon
  '8b1f3383-3814-4ae1-95ae-2f2ee46a276d', // Laura
  '5ff153cf-44e2-4852-86ff-e0b591224f10', // Louise
  '6e571c93-27eb-4112-adf8-3d6ab267261b', // Jonas
  'bc05f9fc-b9bc-4123-8878-f546244a0984', // Christian
  '36e35368-01e4-4dc5-b362-5aafd32d6c2e', // Alex
  '91393a2c-db22-4a8d-bc4e-2a9c955379b6', // Zsolt
]
const CampaignsIgnoreButton = (props) => {
  const { advertiser, ignores, reloadIgnores, networks, channels, showModal } =
    props

  const [allowed, setAllowed] = useState(false)

  useEffect(() => {
    const callback = async () => {
      const response = await getRequest('/profile/v1/me')
      if (allowedUserUuids.includes(response.data.userUuid)) {
        setAllowed(true)
      }
    }
    callback()
  }, [])

  if (!allowed) {
    return null
  }

  return (
    <button
      className={varClass({
        'btn btn-sm text-small': true,
        'btn-dark': !ignores?.length,
        'btn-danger': !!ignores?.length,
      })}
      onClick={() => {
        showModal(
          <IgnoresModal
            advertiser={advertiser}
            ignores={ignores}
            networks={networks}
            channels={channels}
            onDone={() => {
              reloadIgnores()
            }}
          />,

          {
            cardClassName: 'advertiser-ignores-modal',
          }
        )
      }}
    >
      Deactivate campaigns{' '}
      {ignores?.length ? ` (${ignores.length} deactivated)` : ''}
    </button>
  )
}

const checkDate = (newest, oldest, subtractValue, subtractType) => {
  const date = moment().subtract(subtractValue, subtractType)
  return newest > date && oldest < date
}

const CampaignsDateLimitPicker = (props) => {
  const { dateLimit, setDateLimit } = props

  const options = useMemo(() => {
    return ['1w', '1m', '3m', '12m']
  }, [])

  return (
    <div className="advertiser-campaign-date-limit">
      <ul>
        {map(options, (option) => (
          <li
            key={option}
            className={varClass({
              'selected cursor-default': dateLimit === option,
            })}
            onClick={() => {
              if (dateLimit !== option) {
                setDateLimit(option)
              }
            }}
          >
            {option.toUpperCase()}
          </li>
        ))}
        <li
          className={varClass({
            'selected cursor-default': dateLimit === '',
          })}
          onClick={() => {
            if (dateLimit !== '') {
              setDateLimit('')
            }
          }}
        >
          All time
        </li>
      </ul>
    </div>
  )
}

const Table = (props) => {
  const {
    title,
    campaigns,
    allCampaigns,
    advertiserUuid,
    networkDetails,
    search,
    filters,
    channels,
    channelsMap,
    showModal,
    showSnackbar,
    className,
    reloadCampaign,
    campaignReloadRef,
  } = props

  const { sort, dir, toggleSort } = useSortableTable({
    sort: 'networkName',
    dir: 'asc',
  })

  const headProps = { sort, dir, toggleSort }

  const [shownCampaigns, hasMore] = useMemo(() => {
    const [filtered, hasMore] = campaignFilterWithLimit(
      campaigns,
      search,
      filters,
      maxTableLength
    )
    const sorted = campaignSort(filtered, sort, dir)
    return [sorted, hasMore]
  }, [campaigns, search, filters, sort, dir])

  const showCreator = campaigns.some(
    (campaign) =>
      campaign.campaign.campaignIsCreatorDefault ||
      campaign.campaign.campaignIsCreatorOnly
  )
  const showTrafficPercentage = campaigns.some(
    (campaign) =>
      campaign.campaign.campaignIsSplitTraffic ||
      campaign.campaign.campaignIsPartialTraffic
  )

  return (
    <div className={className || ''}>
      <div className="text-dark text-bold m-b-5">{title}</div>
      <div className="card-body-fill relative">
        <div className="table-container-overflow">
          <table className="table table-bodered table-sortable advertiser-campaigns-table">
            <thead className="table-sticky">
              <tr>
                <TableHead
                  value="networkName"
                  label="Campaign"
                  {...headProps}
                />
                <TableHead
                  value="performance"
                  label="Performance 30 days"
                  desc
                  {...headProps}
                />
                <TableHead
                  value="campaignCommission"
                  label="Commission"
                  desc
                  {...headProps}
                />
                <th className="text-nowrap">Sites</th>
                {showCreator && <th className="text-nowrap">Creators</th>}
                {showTrafficPercentage && (
                  <th className="text-nowrap">Traffic</th>
                )}
                <th className="text-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {map(shownCampaigns, (campaign, index) => (
                <TableItem
                  key={`${campaign.campaign.campaignUuid}_${index}`}
                  {...{
                    campaign,
                    allCampaigns,
                    advertiserUuid,
                    networkDetails,
                    channels,
                    channelsMap,
                    search,
                    sort,
                    showCreator,
                    showTrafficPercentage,
                    showModal,
                    showSnackbar,
                    reloadCampaign,
                    campaignReloadRef,
                  }}
                  isLast={index === shownCampaigns.length - 1}
                />
              ))}
              {isBlank(shownCampaigns) ? (
                <tr>
                  <td
                    colSpan={10}
                    className="text-center text-light text-bolder"
                  >
                    Nothing found
                  </td>
                </tr>
              ) : hasMore ? (
                <tr>
                  <td
                    colSpan={10}
                    className="text-center text-light text-bolder"
                  >
                    More campaigns available, please use search campaign field
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      <Tooltip
        id="advertiser-show-opportunity-tooltip"
        type="dark"
        effect="solid"
        place="bottom"
        rebuild={[shownCampaigns]}
      />
    </div>
  )
}

const TableHead = (props) => {
  const { value, label, desc, sort, dir, toggleSort } = props

  return (
    <th
      className={varClass({
        'sort-highlight': sort === value,
      })}
      onClick={toggleSort(value, desc ? 'desc' : 'asc')}
    >
      <span className="column-label text-nowrap">
        {label}
        {sort === value ? (
          <>
            {dir === 'desc' ? (
              <SVG src="/images/insights/caret-down.svg" className="m-l-1" />
            ) : (
              <SVG src="/images/insights/caret-up.svg" className="m-l-1" />
            )}
          </>
        ) : (
          <SVG src="/images/insights/caret.svg" className="m-l-1" />
        )}
      </span>
    </th>
  )
}

const TableItem = (props) => {
  const {
    campaign,
    allCampaigns,
    advertiserUuid,
    networkDetails,
    channels,
    channelsMap,
    search,
    sort,
    showCreator,
    showTrafficPercentage,
    showModal,
    showSnackbar,
    isLast,
    reloadCampaign,
    campaignReloadRef,
  } = props

  const [expandedActionMenu, setExpandedActionMenu] = useState(false)
  const [expandedChannels, setExpandedChannels] = useState(false)
  const [expandedTrackers, setExpandedTrackers] = useState(false)

  // Lazy-load individual tracker rows for this campaign group. The campaigns list
  // endpoint only returns trackersCount; we fetch the full per-tracker data once,
  // on first expand. Cached in component state — but invalidated when the parent
  // bumps `campaignReloadRef` (after Prioritize/Disable/etc submits) so the menu
  // doesn't keep reading stale `trackerIsPriority` flags and invert the toggle.
  const [lazyTrackers, setLazyTrackers] = useState<any[] | null>(null)
  const [lazyTrackersLoading, setLazyTrackersLoading] = useState(false)
  useEffect(() => {
    setLazyTrackers(null)
    setLazyTrackersLoading(false)
  }, [campaignReloadRef])
  useEffect(() => {
    if (
      (!expandedActionMenu && !expandedTrackers) ||
      lazyTrackers !== null ||
      lazyTrackersLoading
    )
      return
    let cancelled = false
    setLazyTrackersLoading(true)
    postRequest(`/advs/v2/advs/${advertiserUuid}/camps/trackers`, {
      networkUuid: campaign.network?.networkUuid,
      connectionUuid: campaign.connection?.connectionUuid,
      programId: campaign.programId || '',
    })
      .then((response) => {
        if (cancelled) return
        setLazyTrackers(response?.data?.trackers || [])
        setLazyTrackersLoading(false)
      })
      .catch(() => {
        if (cancelled) return
        setLazyTrackers([])
        setLazyTrackersLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [
    expandedActionMenu,
    expandedTrackers,
    advertiserUuid,
    campaign.network?.networkUuid,
    campaign.connection?.connectionUuid,
    campaign.programId,
  ])

  const shownExpandedTrackers = useMemo(() => {
    const trackersData = lazyTrackers || []
    const groups = groupBy(trackersData, (tracker) =>
      [
        tracker.trackerUrlMatch,
        tracker.trackerCommissionType,
        tracker.trackerCommissionPercentage,
        tracker.trackerCommissionFlat,
        tracker.trackerIsActive,
        tracker.trackerIsPriority,
        tracker.trackerIsFallback,
        tracker.trackerIsExcluded,
        tracker.trackerAllowInternalPpc,
        tracker.trackerIsSplitTraffic,
        tracker.trackerIsPartialTraffic,
        tracker.trackerTrafficChance,
      ].join(':')
    )
    return Object.values(groups)
      .filter((trackers) => {
        if (search) {
          const query = search.toLowerCase()
          return trackers.some((tracker) =>
            tracker.trackerUrlMatch.includes(query)
          )
        } else {
          return true
        }
      })
      .map((trackers) => ({
        trackerUuids: trackers.map((tracker) => tracker.trackerUuid).sort(),
        trackerChannelUuids: uniq(
          trackers.map((tracker) => tracker.trackerChannelUuid)
        ).sort(),
        trackerUrlMatch: trackers[0].trackerUrlMatch,
        trackerCommissionType: trackers[0].trackerCommissionType,
        trackerCommissionPercentage: trackers[0].trackerCommissionPercentage,
        trackerCommissionFlat: trackers[0].trackerCommissionFlat,
        trackerIsActive: trackers[0].trackerIsActive,
        trackerIsPriority: trackers[0].trackerIsPriority,
        trackerIsFallback: trackers[0].trackerIsFallback,
        trackerIsExcluded: trackers[0].trackerIsExcluded,
        trackerAllowInternalPpc: trackers[0].trackerAllowInternalPpc,
        trackerIsSplitTraffic: trackers[0].trackerIsSplitTraffic,
        trackerIsPartialTraffic: trackers[0].trackerIsPartialTraffic,
        trackerTrafficChance: trackers[0].trackerTrafficChance,
        trackerIsCreatorDefault: Math.max(
          ...trackers.map((tracker) => tracker.trackerIsCreatorDefault)
        ),
        trackerIsCreatorOnly: Math.max(
          ...trackers.map((tracker) => tracker.trackerIsCreatorOnly)
        ),
      }))
  }, [lazyTrackers, search])

  const showEndTrafficPercentage =
    !!campaign.campaign.campaignEndTrafficChanceDate

  // const domainsSingleColumn = campaign.campaign.campaignUrls?.length < 6
  const shownChannels = orderBy(
    campaign.channels,
    ['channelPrioritized', 'channelExcluded', 'channelName'],
    ['desc', 'desc', 'asc']
  )
  if (!expandedChannels) {
    shownChannels.splice(3, shownChannels.length)
  }

  return (
    <>
      <tr className="text-nowrap">
        <td
          className={varClass({
            'sort-highlight': sort === 'networkName',
          })}
        >
          <div className="text-bold m-b-1">
            {campaign.campaign.campaignName || (
              <span className="text-italic">Unknown campaign</span>
            )}
            {campaign.opportunity && (
              <WithTooltip
                className="campaign-alternative inline-block m-l-2"
                title="A new campaign can be found for this advertiser."
                text={
                  <SVG
                    src="/images/recommendations/icon-campaigns.svg"
                    width={18}
                    height={14}
                  />
                }
                id="advertiser-show-opportunity-tooltip"
                forceShow
              />
            )}
            {campaign.alternative && (
              <WithTooltip
                className="campaign-alternative inline-block m-l-2"
                title="A campaign with higher commission can be found for this advertiser."
                text={
                  <SVG
                    src="/images/recommendations/icon-alt-campaigns.svg"
                    width={20}
                    height={14}
                  />
                }
                id="advertiser-show-opportunity-tooltip"
                forceShow
              />
            )}
          </div>
          {campaign.campaign.campaignId && (
            <div className="text-smaller m-t-05">
              <span className="label-light">ID: </span>
              <span className="text-light">{campaign.campaign.campaignId}</span>
            </div>
          )}
          {campaign.network.networkName && (
            <div className="text-smaller m-t-05">
              <span className="label-light">Network: </span>
              <span className="text-light">{campaign.network.networkName}</span>
            </div>
          )}
          {campaign.trackersCount > 0 && (
            <div className="cursor-pointer m-t-2">
              <span
                className="text-light text-smaller text-bold background-new-gray-light p-x-2 p-y-05 border-round"
                onClick={() => setExpandedTrackers(!expandedTrackers)}
              >
                {expandedTrackers ? 'Hide' : 'See'} all subdomains (
                {campaign.trackersCount})
              </span>
            </div>
          )}
        </td>
        {campaign.isConnected ? (
          <>
            <td
              className={varClass({
                'p-y-3': true,
              })}
            >
              <div className="m-b-1">
                <span>Clicks: </span>
                <span className="value-badge value-badge-clickCount">
                  {formatStat(campaign.performance.clicksCount)}
                </span>
              </div>
              <div className="m-b-1">
                <span>Conversions: </span>
                <span className="value-badge value-badge-conversionCount">
                  {formatStat(campaign.performance.conversionsCount)}
                </span>
              </div>
              <div>
                <span>Revenue: </span>
                <span className="value-badge value-badge-conversionCommission">
                  {formatCurrencyReversed(
                    campaign.performance.revenue,
                    campaign.commissions.commissionCurrencyCode || 'DKK'
                  )}
                </span>
              </div>
            </td>
            <td
              className={varClass({
                'p-y-3': true,
                'sort-highlight': sort === 'campaignCommission',
              })}
            >
              <div className="vertical-middle">
                <Commission
                  type={campaign.commissions.commissionType}
                  value={formatCommission(
                    campaign.commissions.commissionType,
                    campaign.commissions.commissionActiveFlat,
                    campaign.commissions.commissionActivePercentage,
                    campaign.commissions.commissionCurrencyCode || 'DKK',
                    true
                  )}
                />
              </div>
            </td>
            <td className="p-3 text-wrap">
              {map(shownChannels, (channel) => (
                <span
                  key={channel.channelUuid}
                  className={varClass({
                    'badge badge-hoverable text-smaller text-nowrap m-05': true,
                    'badge-priority': channel.channelPrioritized,
                    'badge-excluded':
                      channel.channelExcluded && !channel.channelPrioritized,
                  })}
                  onClick={(event) => {
                    navigateTo(event, `/sites/${channel.channelUuid}`)
                  }}
                >
                  {channel.channelPrioritized && (
                    <SVG
                      src="/images/advertisers/icon-priority.svg"
                      title="Prioritized"
                      width={14}
                      height={14}
                      style={{
                        position: 'relative',
                        top: -1,
                        marginRight: 2,
                        marginBottom: -4,
                      }}
                    />
                  )}
                  {channel.channelExcluded && (
                    <SVG
                      src="/images/advertisers/icon-excluded.svg"
                      title="Disabled"
                      width={14}
                      height={14}
                      style={{
                        position: 'relative',
                        top: -1,
                        marginRight: 2,
                        marginBottom: -4,
                      }}
                    />
                  )}
                  {channel.channelName}
                </span>
              ))}
              {!expandedChannels &&
                campaign.channels.length > shownChannels.length && (
                  <span
                    className="badge badge-hoverable text-smaller text-nowrap m-05"
                    onClick={(event) => {
                      setExpandedChannels(!expandedChannels)
                    }}
                  >
                    +{campaign.channels.length - shownChannels.length} channels
                  </span>
                )}
              {campaign.campaign.campaignIsRevshare && (
                <span className="badge badge-hoverable text-smaller text-nowrap m-05">
                  Heylink Boost
                </span>
              )}
            </td>
            {showCreator && (
              <td className="text-left">
                {campaign.campaign.campaignIsCreatorDefault ? (
                  <div className="text-small text-light">
                    Default for creators
                  </div>
                ) : null}
                {campaign.campaign.campaignIsCreatorOnly ? (
                  <div className="text-small text-light">Creators only</div>
                ) : null}
              </td>
            )}
            {showTrafficPercentage && (
              <td className="text-center">
                <div className="row row-center row-fill row-nowrap">
                  <WithTooltip
                    title={
                      showEndTrafficPercentage
                        ? 'Current traffic percentage'
                        : ''
                    }
                    text={
                      campaign.campaign.campaignTrafficChance < 100
                        ? `${campaign.campaign.campaignTrafficChance}%`
                        : null
                    }
                    tooltipId="advertiser-show-opportunity-tooltip"
                    forceShow={showEndTrafficPercentage}
                  />
                  {showEndTrafficPercentage && (
                    <>
                      <SVG
                        width={14}
                        src="/images/overview2/icon-arrow.svg"
                        className="text-light m-x-15"
                      />
                      <WithTooltip
                        className="text-light"
                        title={`Traffic percentage after ${moment(
                          campaign.campaign.campaignEndTrafficChanceDate
                        ).format('YYYY-MM-DD')}`}
                        text={`${campaign.campaign.campaignEndTrafficChance}%`}
                        tooltipId="advertiser-show-opportunity-tooltip"
                        forceShow
                      />
                    </>
                  )}
                </div>
              </td>
            )}
            <td className="static p-r-4">
              {!campaign.campaign.campaignIsRevshare && (
                <>
                  {campaign.campaign.campaignIsActive === 1 ? (
                    <div className="row row-center row-space-between row-nowrap">
                      <div className="row row-center row-narrow row-nowrap">
                        {campaign.campaign.campaignIsPriority ? (
                          <SVG
                            src="/images/advertisers/icon-priority.svg"
                            title="Prioritized"
                          />
                        ) : null}
                        {campaign.campaign.campaignIsExcluded ? (
                          <SVG
                            src="/images/advertisers/icon-excluded.svg"
                            title="Disabled"
                          />
                        ) : null}
                        {campaign.campaign.campaignAllowInternalPpc ? (
                          <SVG
                            src="/images/advertisers/icon-ppc.svg"
                            title="Allow PPC"
                          />
                        ) : null}
                      </div>
                      <TableActionMenu
                        campaign={{
                          ...campaign,
                          trackers: lazyTrackers,
                        }}
                        allCampaigns={allCampaigns}
                        advertiserUuid={advertiserUuid}
                        reloadCampaign={reloadCampaign}
                        showSnackbar={showSnackbar}
                        onOpen={setExpandedActionMenu}
                        isLast={isLast}
                      />
                    </div>
                  ) : (
                    <div className="row row-center row-space-between row-nowrap">
                      <div className="row row-center row-narrow row-nowrap">
                        <SVG
                          src="/images/advertisers/icon-closed.svg"
                          title="Closed"
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
            </td>
          </>
        ) : (
          <>
            <td>-</td>
            <td>-</td>
            <td>-</td>
            {showCreator && <td>-</td>}
            {showTrafficPercentage && <td>-</td>}
            <td className="text-right p-r-4">
              <button
                className="btn btn-dark btn-xs"
                onClick={() => {
                  showModal(
                    <ConnectNetworkModal
                      network={networkDetails.find(
                        (network) =>
                          network.networkUuid === campaign.network.networkUuid
                      )}
                      channels={channels}
                      onSkip={navigateReload}
                      onDone={navigateReload}
                    />
                  )
                }}
              >
                Connect
              </button>
            </td>
          </>
        )}
      </tr>
      {expandedTrackers &&
        map(
          sortBy(shownExpandedTrackers, (tracker) =>
            [
              tracker.trackerIsActive ? 'A' : 'B',
              tracker.trackerUrlMatch,
              tracker.trackerChannelUuids.length,
            ].join(':')
          ),
          (tracker, index) => (
            <TableItemExpanded
              key={tracker.trackerUuids.join(',')}
              tracker={tracker}
              campaign={{ ...campaign, lazyTrackers }}
              channelsMap={channelsMap}
              allCampaigns={allCampaigns}
              advertiserUuid={advertiserUuid}
              reloadCampaign={reloadCampaign}
              showSnackbar={showSnackbar}
              showCreator={showCreator}
              showTrafficPercentage={showTrafficPercentage}
              isLast={index === shownExpandedTrackers.length - 1}
            />
          )
        )}
    </>
  )
}

const TableItemExpanded = (props) => {
  const {
    tracker,
    campaign,
    channelsMap,
    showCreator,
    showTrafficPercentage,
    allCampaigns,
    advertiserUuid,
    reloadCampaign,
    showSnackbar,
    isLast,
  } = props

  const [showAllChannels, setShowAllChannels] = useState(false)
  const shownChannelUuids = showAllChannels
    ? tracker.trackerChannelUuids
    : tracker.trackerChannelUuids.slice(0, 3)

  return (
    <tr key={tracker.trackerUuids.join(',')}>
      <td className="static p-r-4" colSpan={2}>
        <div className="row row-space-between row-center">
          <div className="column column-fill text-smaller">
            <div>
              <span className="label-light">Domain: </span>
              {tracker.trackerUrlMatch}
            </div>
          </div>
        </div>
      </td>
      <td className="text-nowrap">
        <div className="vertical-middle">
          <Commission
            type={tracker.trackerCommissionType}
            value={formatCommission(
              tracker.trackerCommissionType,
              tracker.trackerCommissionFlat,
              tracker.trackerCommissionPercentage,
              campaign.commissions.commissionCurrencyCode || 'DKK',
              true
            )}
          />
        </div>
      </td>
      <td>
        {shownChannelUuids.map((trackerChannelUuid: string) => (
          <div key={trackerChannelUuid} className="text-nowrap">
            <span
              className={varClass({
                'badge badge-hoverable text-smaller text-nowrap m-05': true,
                'badge-priority': tracker.trackerIsPriority,
                'badge-excluded':
                  tracker.trackerIsExcluded && !tracker.trackerIsPriority,
              })}
              onClick={(event) => {
                if (channelsMap[trackerChannelUuid]) {
                  navigateTo(event, `/sites/${trackerChannelUuid}`)
                }
              }}
            >
              {tracker.trackerIsPriority ? (
                <SVG
                  src="/images/advertisers/icon-priority.svg"
                  title="Prioritized"
                  width={14}
                  height={14}
                  style={{
                    position: 'relative',
                    top: -1,
                    marginRight: 2,
                    marginBottom: -4,
                  }}
                />
              ) : null}
              {tracker.trackerIsExcluded ? (
                <SVG
                  src="/images/advertisers/icon-excluded.svg"
                  title="Disabled"
                  width={14}
                  height={14}
                  style={{
                    position: 'relative',
                    top: -1,
                    marginRight: 2,
                    marginBottom: -4,
                  }}
                />
              ) : null}
              {channelsMap[trackerChannelUuid]
                ? channelsMap[trackerChannelUuid].channelName ||
                  'Unnamed channel'
                : 'Deleted channel'}
            </span>
            <a
              href={`${
                process.env.BACKEND_HOST
              }/tr/clicks/v1/${trackerChannelUuid}?targetUrl=${encodeURIComponent(
                'https://' + tracker.trackerUrlMatch
              )}`}
              target="_blank"
              rel="noreferrer"
              className="link link-lighter vertical-middle-self p-05 m-l-1"
              title="Example tracking link"
            >
              <SVG src="/images/icon-outside.svg" className="p-05" />
            </a>
          </div>
        ))}
        {tracker.trackerChannelUuids.length > 3 && (
          <div>
            <span
              className="badge badge-hoverable text-smaller text-nowrap m-05"
              onClick={() => setShowAllChannels(!showAllChannels)}
            >
              {showAllChannels ? 'Show less' : 'Show all sites'}
            </span>
          </div>
        )}
      </td>
      {showCreator && (
        <td className="text-left">
          {tracker.trackerIsCreatorDefault ? (
            <div className="text-small text-light">Default for creators</div>
          ) : null}
          {tracker.trackerIsCreatorOnly ? (
            <div className="text-small text-light">Creators only</div>
          ) : null}
        </td>
      )}
      {showTrafficPercentage && (
        <td className="text-center">
          {tracker.trackerTrafficChance < 100
            ? `${tracker.trackerTrafficChance}%`
            : null}
        </td>
      )}
      <td className="static p-r-4">
        {!campaign.campaign.campaignIsRevshare && (
          <div>
            {tracker.trackerIsActive === 1 ? (
              <div className="row row-center row-space-between row-nowrap">
                <div className="row row-center row-narrow row-nowrap">
                  {tracker.trackerIsPriority ? (
                    <SVG
                      src="/images/advertisers/icon-priority.svg"
                      title="Prioritized"
                    />
                  ) : null}
                  {tracker.trackerIsExcluded ? (
                    <SVG
                      src="/images/advertisers/icon-excluded.svg"
                      title="Disabled"
                    />
                  ) : null}
                  {tracker.trackerAllowInternalPpc ? (
                    <SVG
                      src="/images/advertisers/icon-ppc.svg"
                      title="Allow PPC"
                    />
                  ) : null}
                </div>
                <TableActionMenu
                  campaign={{
                    ...campaign,
                    // Pre-filter to just the trackers in this expanded sub-row so the
                    // TableActionMenu's isTracker=true branch can derive priority/excluded/ppc
                    // flags via .some() over a small subset. Source is the lazy-loaded list,
                    // which is guaranteed available because this row is in the expanded view.
                    trackers: (campaign.lazyTrackers || []).filter((t: any) =>
                      tracker.trackerUuids.includes(t.trackerUuid)
                    ),
                    channels: tracker.trackerChannelUuids
                      .map((channelUuid: string) => channelsMap[channelUuid])
                      .filter(Boolean),
                  }}
                  allCampaigns={allCampaigns}
                  advertiserUuid={advertiserUuid}
                  reloadCampaign={reloadCampaign}
                  showSnackbar={showSnackbar}
                  isTracker
                  isLast={isLast}
                />
              </div>
            ) : (
              <div className="row row-center row-space-between row-nowrap">
                <div className="row row-center row-narrow row-nowrap">
                  <SVG
                    src="/images/advertisers/icon-closed.svg"
                    title="Closed"
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </td>
    </tr>
  )
}

const TableActionMenu = (props) => {
  const {
    campaign,
    allCampaigns,
    advertiserUuid,
    reloadCampaign,
    showSnackbar,
    onOpen,
    isLast,
    isTracker,
  } = props

  const { showModal, hideModal } = useModal()

  const ref = useRef<HTMLDivElement>()

  const [open, setOpen] = useState(false)
  useEffect(() => {
    if (ref.current) {
      const offsetParent = ref.current.offsetParent as HTMLElement
      const top = offsetParent.offsetTop
      const scroll = ref.current.closest('.table-container-overflow').scrollTop
      const offset = isTracker ? 46 : 64
      const refEl = ref.current.querySelector('ul') as HTMLElement
      if (refEl) {
        refEl.style.top = `${top - scroll + offset}px`
      }
    }
    if (onOpen) {
      onOpen(open)
    }
  }, [open])

  useOutsideClick(ref, () => {
    setOpen(false)
  })

  const campaignUuids = useMemo(() => {
    return !isTracker
      ? campaign.campaign.campaignUuids || []
      : campaign.trackers.map((tracker) => tracker.trackerUuid)
  }, [campaign, isTracker])

  const campaignIsPriority = useMemo(() => {
    return !isTracker
      ? campaign.campaign.campaignIsPriority
      : campaign.trackers.some((tracker) => tracker.trackerIsPriority)
  }, [campaign, isTracker])

  const campaignIsExcluded = useMemo(() => {
    return !isTracker
      ? campaign.campaign.campaignIsExcluded
      : campaign.trackers.some((tracker) => tracker.trackerIsExcluded)
  }, [campaign, isTracker])

  const campaignAllowInternalPpc = useMemo(() => {
    return !isTracker
      ? campaign.campaign.campaignAllowInternalPpc
      : campaign.trackers.some((tracker) => tracker.trackerAllowInternalPpc)
  }, [campaign, isTracker])

  const canSplitTest =
    uniq(allCampaigns.map((campaign) => campaign.connection.connectionUuid))
      .length > 1 && campaign.channels.length > 0
  const haveSplitTest = allCampaigns.some(
    (campaign) =>
      campaign.campaign.campaignIsSplitTraffic ||
      campaign.campaign.campaignIsPartialTraffic
  )

  const handlePrioritize = async () => {
    showModal(
      <PrioritizeModal
        hideModal={hideModal}
        campaign={campaign}
        showSnackbar={showSnackbar}
        reloadCampaign={reloadCampaign}
      />,
      {
        cardClassName: 'prioritize-modal',
      }
    )
  }
  const handleUnprioritize = async () => {
    showModal(
      <PrioritizeModal
        hideModal={hideModal}
        campaign={campaign}
        showSnackbar={showSnackbar}
        reloadCampaign={reloadCampaign}
      />,
      {
        cardClassName: 'prioritize-modal',
      }
    )
  }
  const handleDisable = async () => {
    showModal(
      <DisableModal
        hideModal={hideModal}
        campaign={campaign}
        showSnackbar={showSnackbar}
        reloadCampaign={reloadCampaign}
      />,
      {
        cardClassName: 'prioritize-modal',
      }
    )
  }
  const handleEnable = async () => {
    showModal(
      <DisableModal
        hideModal={hideModal}
        campaign={campaign}
        showSnackbar={showSnackbar}
        reloadCampaign={reloadCampaign}
      />,
      {
        cardClassName: 'prioritize-modal',
      }
    )
  }

  const handleSplit = () => {
    showModal(
      <SplitTestModal
        hideModal={hideModal}
        campaign={campaign}
        allCampaigns={allCampaigns}
        advertiserUuid={advertiserUuid}
        haveSplitTest={haveSplitTest}
        showSnackbar={showSnackbar}
        reloadCampaign={reloadCampaign}
      />,
      {
        cardClassName: 'abtest-modal',
      }
    )
  }

  const handleEditCreators = () => {
    showModal(
      <CreatorModal
        hideModal={hideModal}
        campaign={campaign}
        showSnackbar={showSnackbar}
        reloadCampaign={reloadCampaign}
      />,
      {
        cardClassName: 'creator-modal',
      }
    )
  }

  const handleAllowPpc = () => {
    showModal(
      <AllowPpcModal
        hideModal={hideModal}
        campaign={campaign}
        showSnackbar={showSnackbar}
        reloadCampaign={reloadCampaign}
      />,
      {
        cardClassName: 'prioritize-modal',
      }
    )
  }

  const isReady = campaign.trackers?.length > 0

  return (
    <div
      className={varClass({
        'advertiser-campaigns-actions': true,
        'open': open,
        'last': isLast,
      })}
      ref={ref}
    >
      <button className="link" onClick={() => setOpen(!open)}>
        <SVG src="/images/advertisers/icon-dots.svg" />
      </button>
      <ul>
        <SmallLoadable
          loaded={isReady}
          className="m-x-60 m-y-2"
          style={{ width: '16px', height: '16px' }}
        >
          <li>
            <button
              className="link"
              onClick={(event) => {
                navigateTo(event, '/tools/urls')
              }}
            >
              Create Smart Link
            </button>
          </li>
          {canSplitTest && (
            <li>
              <button
                className="link"
                onClick={() => {
                  setOpen(false)
                  handleSplit()
                }}
              >
                {haveSplitTest ? 'Update' : 'Create'} split test
              </button>
            </li>
          )}
          {campaignUuids.length > 0 && (
            <li>
              <button
                className="link"
                onClick={() => {
                  setOpen(false)
                  handleEditCreators()
                }}
              >
                Edit creators
              </button>
            </li>
          )}
          {campaignUuids.length > 0 && (
            <li>
              <button
                className="link"
                onClick={() => {
                  setOpen(false)
                  handleAllowPpc()
                }}
              >
                {campaignAllowInternalPpc ? 'Edit PPC' : 'Allow PPC'}
              </button>
            </li>
          )}
          {campaignUuids.length > 0 && (
            <>
              {campaignIsPriority ? (
                <li>
                  <button
                    className="link"
                    onClick={async () => {
                      setOpen(false)
                      handleUnprioritize()
                    }}
                  >
                    Unprioritize
                  </button>
                </li>
              ) : (
                <li>
                  <button
                    className="link"
                    onClick={async () => {
                      setOpen(false)
                      handlePrioritize()
                    }}
                  >
                    Prioritize
                  </button>
                </li>
              )}
              {campaignIsExcluded ? (
                <li>
                  <button
                    className="link"
                    onClick={async () => {
                      setOpen(false)
                      handleEnable()
                    }}
                  >
                    Enable
                  </button>
                </li>
              ) : (
                <li>
                  <button
                    className="link"
                    onClick={async () => {
                      setOpen(false)
                      handleDisable()
                    }}
                  >
                    Disable
                  </button>
                </li>
              )}
            </>
          )}
        </SmallLoadable>
      </ul>
    </div>
  )
}

const ClosedCampaignTable = (props) => {
  const {
    title,
    campaigns,
    networkDetails,
    search,
    filters,
    channels,
    showModal,
    showSnackbar,
    className,
    reloadCampaign,
  } = props

  const { sort, dir, toggleSort } = useSortableTable({
    sort: 'networkName',
    dir: 'asc',
  })

  const headProps = { sort, dir, toggleSort }

  const [shownClosedCampaigns, hasMore] = useMemo(() => {
    const [filtered, hasMore] = campaignFilterWithLimit(
      campaigns,
      search,
      filters
    )
    const grouped = groupBy(
      campaignSort(filtered, sort, dir),
      (item: any) => item.network.networkUuid
    )
    return [
      Object.values(grouped).map((campaigns: any) => ({
        network: campaigns[0].network,
        campaigns: campaigns,
      })),
      hasMore,
    ]
  }, [search, filters, sort, dir])

  return (
    <div className={className || ''}>
      <div className="text-dark text-bold m-b-5">{title}</div>
      <div className="card-body-fill relative">
        <div className="table-container-overflow">
          <table className="table table-bodered table-sortable advertiser-campaigns-table">
            <thead className="table-sticky">
              <tr>
                <th className="text-nowrap">Campaign</th>
                <th className="text-nowrap">Performance 30 days</th>
                <th className="text-nowrap">Commission</th>
                <th className="text-nowrap">Sites</th>
              </tr>
            </thead>
            <tbody>
              {map(shownClosedCampaigns, (campaign) => (
                <ClosedCampaignTableItem
                  key={`${campaign.network.networkUuid}}`}
                  {...{
                    campaign,
                    networkDetails,
                    channels,
                    sort,
                    showModal,
                    showSnackbar,
                    reloadCampaign,
                  }}
                />
              ))}
              {isBlank(shownClosedCampaigns) && (
                <tr>
                  <td
                    colSpan={10}
                    className="text-center text-light text-bolder"
                  >
                    Nothing found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Tooltip
        id="advertiser-show-opportunity-tooltip"
        type="dark"
        effect="solid"
        place="bottom"
        rebuild={[shownClosedCampaigns]}
      />
    </div>
  )
}

const ClosedCampaignTableItem = (props) => {
  const { campaign } = props
  const [expanded, setExpanded] = useState(false)

  const expandCampaigns = useMemo(
    () => campaignSort(campaign.campaigns, 'networkName', 'asc'),
    [campaign]
  )

  return (
    <>
      <tr>
        <td colSpan={6}>
          <div className="row row-space-between row-center">
            <div className="text-bold">
              <span className="label-light">Network: </span>
              <span>{campaign.network.networkName}</span>
            </div>
            <div
              className="cursor-pointer text-smaller text-light"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? 'Hide campaigns' : 'Show campaigns'}
            </div>
          </div>
        </td>
      </tr>
      {expanded &&
        map(expandCampaigns, (c, index) => (
          <tr key={index}>
            <td>
              <div className="text-bold m-b-1">
                {c?.campaign?.campaignName || (
                  <span className="text-italic">Unknown campaign</span>
                )}
                {c.opportunity && (
                  <WithTooltip
                    className="campaign-alternative inline-block m-l-2"
                    title="A new campaign can be found for this advertiser."
                    text={
                      <SVG
                        src="/images/recommendations/icon-campaigns.svg"
                        width={18}
                        height={14}
                      />
                    }
                    id="advertiser-show-opportunity-tooltip"
                    forceShow
                  />
                )}
                {c.alternative && (
                  <WithTooltip
                    className="campaign-alternative inline-block m-l-2"
                    title="A campaign with higher commission can be found for this advertiser."
                    text={
                      <SVG
                        src="/images/recommendations/icon-alt-campaigns.svg"
                        width={20}
                        height={14}
                      />
                    }
                    id="advertiser-show-opportunity-tooltip"
                    forceShow
                  />
                )}
              </div>
              {c.campaign.campaignId && (
                <div className="text-smaller m-t-05">
                  <span className="label-light">ID: </span>
                  <span className="text-light">{c.campaign.campaignId}</span>
                </div>
              )}
            </td>
            {c.isConnected ? (
              <>
                <td
                  className={varClass({
                    'p-y-3': true,
                  })}
                >
                  <div className="m-b-1">
                    <span>Clicks: </span>
                    <span className="value-badge value-badge-clickCount">
                      {formatStat(c.performance.clicksCount)}
                    </span>
                  </div>
                  <div className="m-b-1">
                    <span>Conversions: </span>
                    <span className="value-badge value-badge-conversionCount">
                      {formatStat(c.performance.conversionsCount)}
                    </span>
                  </div>
                  <div>
                    <span>Revenue: </span>
                    <span className="value-badge value-badge-conversionCommission">
                      {formatCurrencyReversed(
                        c.performance.revenue,
                        c.commissions.commissionCurrencyCode || 'DKK'
                      )}
                    </span>
                  </div>
                </td>
                <td
                  className={varClass({
                    'p-y-3': true,
                  })}
                >
                  <div className="vertical-middle">
                    <Commission
                      type={c.commissions.commissionType}
                      value={formatCommission(
                        c.commissions.commissionType,
                        c.commissions.commissionFlat,
                        c.commissions.commissionPercentage,
                        c.commissions.commissionCurrencyCode || 'DKK',
                        true
                      )}
                    />
                  </div>
                </td>
                <td className="p-3 text-wrap">
                  {map(sortBy(c.channels, 'channelName'), (channel) => (
                    <span
                      key={channel.channelUuid}
                      className="badge badge-hoverable text-smaller text-nowrap m-05"
                      onClick={(event) => {
                        navigateTo(event, `/sites/${channel.channelUuid}`)
                      }}
                    >
                      {channel.channelName}
                    </span>
                  ))}
                </td>
              </>
            ) : (
              <>
                <td>-</td>
                <td>-</td>
                <td>-</td>
              </>
            )}
          </tr>
        ))}
    </>
  )
}

const TrafficTable = (props) => {
  const { title, data, channels, className } = props

  const { sort, dir, toggleSort } = useSortableTable({
    sort: 'clickCount',
    dir: 'desc',
  })

  const headProps = { sort, dir, toggleSort }

  const shownRows = trafficSort(data.rows, sort, dir)

  return (
    <div className={className || ''}>
      <div className="row row-center row-space-between m-b-5">
        <div className="text-dark text-bold">{title}</div>
        <div className="text-light text-small">
          {data?.rows?.length || 0} {plural(data?.rows?.length || 0, 'page')}
        </div>
      </div>
      <div className="card-body-fill">
        <div className="table-container-overflow">
          <table className="table table-bodered table-sortable advertiser-campaigns-table">
            <thead className="table-sticky">
              <tr>
                <TableHead value="grp" label="Page" {...headProps} />
                <TableHead value="clickCount" label="Clicks" {...headProps} />
                <TableHead
                  value="conversionCount"
                  label="Conversions"
                  {...headProps}
                />
                <TableHead
                  value="conversionCommission"
                  label="Revenue"
                  {...headProps}
                />
                {/*<th className="text-nowrap">Actions</th>*/}
              </tr>
            </thead>
            <tbody>
              {map(shownRows, (row, index) => (
                <TrafficTableItem
                  key={row.grp}
                  {...{
                    row,
                    sort,
                    dir,
                    channels,
                  }}
                  isLast={index === shownRows.length - 1}
                />
              ))}
              {isBlank(shownRows) && (
                <tr>
                  <td
                    colSpan={10}
                    className="text-center text-light text-bolder"
                  >
                    Nothing found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

const TrafficTableItem = (props) => {
  const { row, sort } = props

  const { grp, name } = row
  const values = calculateValues(row)

  return (
    <tr className="text-nowrap">
      <td
        className={varClass({
          'sort-highlight': sort === 'grp',
        })}
      >
        <div className="text-bold m-b-1">{name || grp || 'Unknown'}</div>
        {/*
        <div>
          <span className="label-light">Channel: </span>
          <span className="text-light">{'?!?!?'}</span>
        </div>
        */}
      </td>
      <td
        className={varClass({
          'sort-highlight': sort === 'clickCount',
        })}
      >
        <div className="value-badge value-badge-clickCount">
          {values.clickCount}
        </div>
      </td>
      <td
        className={varClass({
          'sort-highlight': sort === 'conversionCount',
        })}
      >
        <div className="value-badge value-badge-conversionCount">
          {values.conversionCount}
        </div>
      </td>
      <td
        className={varClass({
          'sort-highlight': sort === 'conversionCommission',
        })}
      >
        <div className="value-badge value-badge-conversionCommission">
          {values.conversionTotalCommission || values.conversionCommission}
        </div>
      </td>
    </tr>
  )
}

const CampaignHistory = (props) => {
  const { data, campaigns, channels, networks } = props

  const [filters, setFilters] = useState([
    {
      key: 'channelUuids',
      value: '',
      label: '',
    },
    {
      key: 'networkUuids',
      value: '',
      label: '',
    },
  ])

  const [dateLimit, setDateLimit] = useState('1m')
  const selectedDateLimit = useMemo(() => {
    if (dateLimit) {
      const value: number = parseInt(
        dateLimit.substring(0, dateLimit.length - 1)
      )
      let type: 'day' | 'week' | 'month' = 'day'
      switch (dateLimit[dateLimit.length - 1]) {
        case 'w':
          type = 'week'
          break
        case 'm':
          type = 'month'
          break
      }

      return moment().subtract(value, type).format('YYYY-MM-DD')
    } else {
      return null
    }
  }, [dateLimit])

  const filterReset =
    filters
      .filter((filter) => filter.value)
      .map((filter) => filter.value)
      .join(',') + dateLimit

  const shownEvents = useMemo(() => {
    if (data?.events) {
      return data.events.filter((event) => {
        if (selectedDateLimit) {
          if (event.event.eventDatetime.substring(0, 10) < selectedDateLimit)
            return false
        }
        for (const filter of filters) {
          if (filter.value) {
            switch (filter.key) {
              case 'channelUuid':
                if (event.channel[filter.key] !== filter.value) return false
                break
              case 'networkUuid':
                if (event.network[filter.key] !== filter.value) return false
                break
              default:
            }
          }
        }
        return true
      })
    } else {
      return []
    }
  }, [filterReset])

  const groupedByCampaign = useMemo(() => {
    const grouped = groupBy(campaigns, (e) => e.campaign.campaignId)
    return Object.keys(grouped)
      .map((key) => {
        const campaignUuids = grouped[key]
          .map((item) => item.campaign.campaignUuids)
          .flat()
        const events = shownEvents.filter((item) =>
          campaignUuids.some((uuid) => uuid === item.campaign.campaignUuid)
        )
        if (!events.length) return undefined
        return {
          campaign: grouped[key][0].campaign,
          network: grouped[key][0].network,
          events,
        }
      })
      .filter((v) => v)
  }, [campaigns, shownEvents])

  return (
    <div>
      <div className="row row-center row-space-between">
        <div className="vertical-middle m-b-7">
          <CampaignsFilter {...{ filters, setFilters, channels, networks }} />
        </div>
        <div>
          <CampaignsDateLimitPicker {...{ dateLimit, setDateLimit }} />
        </div>
      </div>

      <div className="text-dark text-bold m-t-30 m-b-5">Change log</div>
      <div className="card-body-fill">
        <div className="table-container-overflow">
          <table className="table table-bodered table-sortable advertiser-campaigns-table">
            <thead className="table-sticky">
              <tr>
                <th>Campaign</th>
                <th>Action</th>
                <th>Change</th>
                <th>Site</th>
                <th className="text-right">Time</th>
              </tr>
            </thead>
            <tbody>
              {!isBlank(shownEvents) ? (
                <>
                  {map(groupedByCampaign, (item) => (
                    <GroupEvent
                      key={item.campaign.campaignId}
                      groupEvent={item}
                    />
                  ))}
                </>
              ) : (
                <tr>
                  <td
                    colSpan={10}
                    className="text-center text-light text-bolder"
                  >
                    Nothing found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

const GroupEvent = (props) => {
  const { groupEvent } = props
  const [expanded, setExpanded] = useState(false)

  return (
    <>
      <tr>
        <td colSpan={5} className="p-l-40">
          <div className="row row-space-between row-center">
            <div>
              <HistoryCampaign
                network={groupEvent.network}
                campaign={groupEvent.campaign}
              />
            </div>
            <div
              className="cursor-pointer text-smaller text-light"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? 'Hide events' : 'Show events'}
            </div>
          </div>
        </td>
      </tr>
      {expanded &&
        map(groupEvent.events, (event, index) => (
          <tr key={index}>
            <td className="p-l-40">
              <HistoryCampaign
                network={groupEvent.network}
                campaign={groupEvent.campaign}
              />
            </td>
            <td className="text-wrap text-capitalize">{event.event.event}</td>
            <td>
              <HistoryEvent event={event.event} />
            </td>
            <td className="text-wrap">
              <span
                className="badge badge-hoverable text-smaller text-nowrap"
                onClick={(e) => {
                  navigateTo(e, `/sites/${event.channel.channelUuid}`)
                }}
              >
                {event.channel.channelName}
              </span>
            </td>
            <td className="text-right">{event.event.eventDatetime}</td>
          </tr>
        ))}
    </>
  )
}

export const HistoryCampaign = (props) => {
  const { campaign, network } = props

  return (
    <>
      <div className="text-bold p-l-1 m-b-05">{campaign.campaignName}</div>
      <div className="row" style={{ gap: '2px 16px' }}>
        {campaign?.campaignId && (
          <div className="p-l-1 text-smaller">
            <span className="label-light">ID: </span>
            <span className="text-light">{campaign.campaignId}</span>
          </div>
        )}
        {network.networkName && (
          <div className="p-l-1 text-smaller">
            <span className="label-light">Network: </span>
            <span className="text-light">{network.networkName}</span>
          </div>
        )}
      </div>
    </>
  )
}

export const HistoryEvent = (props) => {
  const { event } = props

  if (event.fromIsActive !== event.toIsActive) {
    if (event.toIsActive) {
      return (
        <span className="badge badge-rounder text-smaller text-bold text-nowrap text-new-green">
          Campaign created
        </span>
      )
    } else {
      return (
        <span className="badge badge-rounder text-smaller text-bold text-nowrap text-light">
          Campaign closed
        </span>
      )
    }
  }

  const fromValue = event.fromCommissionFlat || event.fromCommissionPercentage
  const toValue = event.toCommissionFlat || event.toCommissionPercentage
  const isPositive = toValue > fromValue

  return (
    <span
      className={varClass({
        'campaign-change text-smaller text-bold text-nowrap vertical-middle':
          true,
      })}
    >
      <span
        className="text-primary text-bold"
        style={{ position: 'relative', top: -0.5 }}
      >
        {formatCommission(
          '',
          event.fromCommissionFlat,
          event.fromCommissionPercentage,
          event.currencyCode || 'DKK',
          true
        )}
      </span>
      <SVG
        src="/images/advertisers/chevron-right.svg"
        className="m-x-05"
        style={{ position: 'relative', top: -0.5 }}
      />
      <span
        className={varClass({
          'campaign-change-new-value vertical-middle': true,
          'text-new-green': isPositive,
          'text-new-blood': !isPositive,
        })}
      >
        {formatCommission(
          '',
          event.toCommissionFlat,
          event.toCommissionPercentage,
          event.currencyCode || 'DKK',
          true
        )}
        {isPositive ? (
          <SVG
            src="/images/caret-up.svg"
            width={8}
            height={4}
            className="m-l-1 m-t--05"
          />
        ) : (
          <SVG
            src="/images/caret-down.svg"
            width={8}
            height={4}
            className="m-l-1"
          />
        )}
      </span>
    </span>
  )
}
